// firstRunFlow.js — THE FIRST CONVERSATION (Braeden 8/14)
//
//   "i want the user to click first time, and aria will talk to them and i want
//    them to have a conversation with them, aria needs to have responses ready
//    to go so its almost like an instant conversation, than she needs to direct
//    the user what to do based on what they want"
//
// Every line here is already on the client, so her next sentence starts the
// instant the user stops talking — no round trip, no "thinking" gap. That is
// the whole point: a first impression that stalls for two seconds reads as a
// slow robot no matter how good the words are.
//
// WHERE THIS SITS AGAINST THE "NO CANNED ANSWERS" RULE (8/12): staged lines
// cover the GUIDED path only — the handful of turns where she is driving and
// already knows what she needs to ask next. The moment the user says something
// off-script, `match` returns null and the turn goes to the brain like any
// other. She is never stuck answering from a script, she just doesn't stall on
// the four questions she asks every single new person.
//
// One question per turn. Never two. (Locked doctrine.)

// "no problem" / "no worries" are agreement, not refusal — they read as NO to
// any naive matcher, which is exactly the class of miss he called out (8/12:
// "she needs to know how to read anything not just yes or no").
const YES_DESPITE_NO = /\bno (problem|worries|doubt)\b/i;
// "yes" was missing from this list for a full day. \by\b does not match inside
// "yes", so the single most common affirmative in English fell through to the
// brain on every turn (caught 8/14 walking the new opening).
const YES = /\b(y|ya|yah|yes|yeah|yep|yup|yeh|aye|sure|ok|okay|k|right|correct|course|absolutely|definitely|please|sounds good|that works|go ahead|do it|lets go|let's go|loud and clear|i can|i do|uh huh|mhm)\b/i;
const NO = /\b(n|no|nah|nope|not really|dont|don't|do not|negative|pass|skip|later|not now|im good|i'm good|no thanks)\b/i;

// Trades she can set up today. Order matters — longer phrases first so
// "junk removal" wins over a bare "removal".
const TRADES = [
  // "i haul junk" put the verb before the noun and slipped through a
  // junk-then-haul pattern. Either word on its own is enough here — nobody
  // answers "what kind of work do you do" with "junk" meaning anything else.
  ['junk removal', /\bjunk\b|\bhaul(s|ed|ing|er|ers)?\b|dump run|clean ?outs?/i],
  ['landscaping', /\blandscap|lawn ?care|mowing|yard work\b/i],
  ['plumbing', /\bplumb|drain|water heater|sewer\b/i],
  ['electrical', /\belectric|wiring|electrician\b/i],
  ['HVAC', /\bhvac|heating|air ?condition|furnace|ac repair\b/i],
  ['roofing', /\broof/i],
  ['cleaning', /\bclean(ing|er)|maid|janitor/i],
  ['painting', /\bpaint/i],
  ['moving', /\bmovers?|moving\b/i],
  ['pressure washing', /\bpressure ?wash|power ?wash/i],
  ['tree work', /\btree (service|removal|trim)|arborist\b/i],
  ['handyman', /\bhandy ?man|repairs?\b/i],
  ['concrete', /\bconcrete|masonry|driveway\b/i],
  ['towing', /\btow(ing)?\b/i],
  ['pest control', /\bpest|extermin/i],
  ['pool service', /\bpool\b/i],
  ['auto repair', /\bauto|mechanic|body shop\b/i],
  ['salon', /\bsalon|barber|hair|nails?\b/i],
  ['restaurant', /\brestaurant|food truck|catering|cafe\b/i],
  ['photography', /\bphoto|videograph\b/i],
];

// "what can you do" — the one time she SHOULD list herself. Asked, not pushed.
const WANTS_A_MENU = /\b(what can you do|what do you do|what are you (for|good for)|how can you help|what can you help|help me with|what do you offer|options|like what|such as|show me what)\b/i;
// They opened with nothing to go on. Not a problem to solve — she just tells
// them what she is for. "idk" is a person waiting to be given a reason.
const NOTHING_YET = /^(i (don'?t|dunno) know|idk|dunno|not sure|nothing( really)?|no idea|um+|uh+|hmm+|\?+)[\s.!?]*$/i;

// A web address the way people actually say or type one: "acmeroofing.com",
// "www.acme.com", "https://acme.com", or "acme dot com" out loud. Deliberately
// requires a real TLD so "i do roofing" is never mistaken for a domain.
const TLD = 'com|net|org|co|io|biz|us|info|shop|site|online|services|company';
const DOMAIN = new RegExp(`\\b((?:https?:\\/\\/)?(?:www\\.)?[a-z0-9][a-z0-9-]{0,62}(?:\\.[a-z0-9-]{1,63})*\\.(?:${TLD})(?:\\/[^\\s]*)?)\\b`, 'i');
const SPOKEN_DOT = new RegExp(`\\b([a-z0-9][a-z0-9 -]{0,62})\\s+dot\\s+(${TLD})\\b`, 'i');

export function readWebAddress(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  const m = t.match(DOMAIN);
  if (m) return m[1];
  // spoken: "acme roofing dot com" -> acmeroofing.com
  const s = t.match(SPOKEN_DOT);
  if (s) return s[1].replace(/\s+/g, '').toLowerCase() + '.' + s[2].toLowerCase();
  return null;
}

export function detectTrade(text) {
  const t = String(text || '');
  for (const [name, re] of TRADES) if (re.test(t)) return name;
  return null;
}

/** yes / no / null — reads the way people actually answer, not just y/n. */
export function readYesNo(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  if (YES_DESPITE_NO.test(t)) return 'yes';
  // NO first: "no thanks" carries no yes-word, and a leading "no" must beat a
  // trailing "sure".
  if (NO.test(t)) return 'no';
  if (YES.test(t)) return 'yes';
  return null;
}

/**
 * The graph. Each node:
 *   say      her line — spoken and shown, instantly
 *   next(t)  given what the user said, return { node } or { action } or null
 *            null  → hand this turn to the brain, then come back to `node`
 */
export const NODES = {
  // ── SHE IS AN ASSISTANT. SHE IS NOT RUNNING A SCRIPT AT THEM. ───────────
  //
  //   "its kinda wired how she talks, just want her to be an assitant more or
  //    less, like what can i do, or just be there when they talk to you"
  //
  // WHAT WAS WEIRD, AND WHY IT IS GONE (8/15):
  //   "Are you familiar with how AI works?"  - a quiz. Nobody opens a working
  //        relationship by testing you. It was MY idea of useful context, paid
  //        for out of his patience on turn two.
  //   "Ask me anything about computers"      - a party trick. It made her the
  //        exhibit instead of the help, and it put the burden of thinking up a
  //        question onto someone who came here with an actual problem.
  //   "Anything else you want to throw at me?" - showing off.
  //   "so if you run one I can show you that too" - a pitch, dressed as an offer.
  //
  // Four turns of her agenda before he could say a single thing he came to say.
  // An assistant says who she is, says she is here, and then SHUTS UP. The
  // person leads. She only asks a question when she genuinely needs the answer
  // to do something - which is the same one-question rule as everywhere else.
  // ── THE OPENING, AS HE SCRIPTED IT (8/16) ───────────────────────────────
  //
  //   "when she asks can you hear me, user answers, she says awesome, are you
  //    fimiliar with ai? No worries, (or great depending on response) i can
  //    look up anythhing for you but my best feature is helping run automations
  //    for businesses, want me to show you?"
  //
  // The AI question is BACK, and this is why it works now where it did not on
  // 8/14. Then it was a quiz that led to a party trick - "ask me anything about
  // computers" - so the answer bought the person nothing and he called it
  // weird. Here the answer is used the moment it is given: it picks how she
  // opens the next sentence ("no worries" against "great"), and that sentence
  // goes straight to what she is FOR. Same question, and now it earns its turn.
  greet: {
    // "this is Aria", NOT "I'm Aria". Edge-TTS fuses "Hey, I'm Aria" into
    // "Ay, aye, Ayvaria" — verified 8/12 by round-tripping her own audio back
    // through her own STT. "this is Aria" survives her mouth verbatim, and it
    // is how you answer a phone anyway. I had written "I'm Aria" straight back
    // into the flow on 8/15, which would have made the FIRST WORDS she ever
    // says to a new person unintelligible (8/16).
    say: "Hey, this is Aria. Can you hear me okay?",
    next(text) {
      const yn = readYesNo(text);
      if (yn === 'no') return { node: 'cant_hear' };
      if (yn === 'yes') return { node: 'ai_familiar' };
      return null;
    },
    fallbackTo: 'ai_familiar',
  },

  ai_familiar: {
    say: 'Awesome. Are you familiar with AI?',
    next(text) {
      const t = String(text || '').toLowerCase();
      // ORDER MATTERS and so do the word boundaries. Without \b the "no" inside
      // "not really" and the "some" inside "somewhat" fire the wrong branch.
      // Least-experience first, because "not really" contains words the other
      // two branches would otherwise claim (8/14).
      if (/\b(not at all|not really|no|nope|never|zero|nothing|clueless|haven'?t|first time|new to (this|it))\b/.test(t)) {
        return { node: 'pitch', data: { level: 'none' } };
      }
      if (/\b(a little|a bit|kinda|kind of|somewhat|a few times|played (with|around)|tried it|chatgpt|used it once)\b/.test(t)) {
        return { node: 'pitch', data: { level: 'some' } };
      }
      if (/\b(yes|yeah|yep|very|pretty|quite|use it|daily|every ?day|all the time|i build|developer|work with|for years)\b/.test(t)) {
        return { node: 'pitch', data: { level: 'lots' } };
      }
      return null;
    },
    fallbackTo: 'pitch',
  },

  // She names the thing she is best at and asks for permission to show it.
  // ONE question, no "or", and the offer is hers to make once - if they decline
  // she stops, because a second ask is a sales call.
  pitch: {
    say: (d) => (
      (d.level === 'none' ? 'No worries. ' : d.level === 'lots' ? 'Great. ' : 'Good. ')
      + "What I'm best at is running the office side of a business. Want to see?"
    ),
    next(text) {
      // They skipped ahead and just told her the trade — take it and go look.
      const trade = detectTrade(text);
      if (trade) return { action: 'matchFeatures', data: { trade } };
      const yn = readYesNo(text);
      if (yn === 'yes') return { node: 'work' };
      if (yn === 'no') return { node: 'standby' };
      return null;
    },
    fallbackTo: 'pitch',
  },

  // ── SHE DOES SOMETHING, SHE DOESN'T JUST DESCRIBE IT (Braeden 8/16) ─────
  //
  //   "she needs to run some actions pretty quick, so when they answer the work
  //    question, she needs to do an action to show what she can do ... can you
  //    type in your website on the search bar, she finds it pulls it up in like
  //    a browser looking thing, so we can see the website and she can do her
  //    part"
  //
  // Straight after she names what she'd do for their trade, she asks for the
  // one thing that lets her prove it. Telling someone you can manage their
  // website is a claim; putting their own website on the screen ten seconds
  // into the conversation is evidence.
  ask_site: {
    say: "What's your website? Type it in and I'll pull it up.",
    next(text) {
      const t = String(text || '').trim();
      const url = readWebAddress(t);
      if (url) return { action: 'showSite', data: { url } };
      // No site is a completely normal answer for a trade business, and it is
      // the opening for the thing she can build them. Not a failure.
      if (/\b(no|none|dont have|don'?t have|havent|haven'?t|nope|not yet|nothing)\b/i.test(t)) {
        return { node: 'no_site' };
      }
      return null;
    },
    fallbackTo: 'ask_site',
  },

  no_site: {
    say: "No problem — I build those. Want one?",
    next(text) {
      const yn = readYesNo(text);
      if (yn === 'yes') return { action: 'openBusinessSetup' };
      if (yn === 'no') return { node: 'standby' };
      return null;
    },
    fallbackTo: 'no_site',
  },

  // "if they say no, ok you say the word and im here" — she takes no for an
  // answer, in one line, and does not ask anything else.
  standby: {
    say: "Say the word.",
    next() { return { action: 'end' }; },
  },

  // ── THE THINKING TURN ───────────────────────────────────────────────────
  //   "aria needs to think and find the features that could apply to this
  //    person (if it takes a while for her to think she needs to say that,
  //    Im going through the library to see the best way to help your situation"
  //
  // The ACTION fires the instant they answer. My first attempt routed to a
  // holding node with no line, whose next() only runs when the user speaks
  // again — so she would have asked what he does for work and then sat there
  // in silence waiting for him to say something else.
  work: {
    say: 'Ok, what do you do for work?',
    next(text) {
      const trade = detectTrade(text);
      if (trade) return { action: 'matchFeatures', data: { trade } };
      // Not a trade she knows, but they answered — take them at their word and
      // still go and look. An unlisted trade is a person, not an error.
      const t = String(text || '').trim();
      if (t.length > 2 && !isTooRichForACannedLine(t)) {
        return { action: 'matchFeatures', data: { trade: t.slice(0, 40) } };
      }
      return null;
    },
    fallbackTo: 'work',
  },

  // Where she sits once the conversation is his. No script, no next question -
  // every turn goes to the brain until he says something she can act on.
  open: {
    say: null,
    next(text) {
      const trade = detectTrade(text);
      if (trade) return { node: 'confirm_build', data: { trade } };
      if (WANTS_A_MENU.test(String(text || ''))) return { node: 'menu' };
      return null;
    },
    fallbackTo: 'open',
  },

  // ONLY WHEN THEY ASK, or when they clearly have no idea where to start. She
  // names what she does in plain words and stops - no question on the end,
  // because he will ask if he wants more ("if they want to know something they
  // will ask").
  menu: {
    say: "I run the office side. Here's what that covers.",
    next(text) {
      const trade = detectTrade(text);
      if (trade) return { node: 'confirm_build', data: { trade } };
      return null;
    },
    fallbackTo: 'open',
  },

  // Kept, but no longer a node she WALKS people into - "can you hear me?" is
  // not something an assistant asks. It is now only reached when the person
  // actually says they cannot hear her, from wherever they are in the thread,
  // and the repair ladder in ariaVoiceHealth.js takes it from there.
  cant_hear: {
    say: "No problem, we can do this by text.",
    next(text) { return NODES.open.next(text); },
    fallbackTo: 'open',
  },

  // WHY SHE ASKS THIS AT ALL: it is the experience check. How much someone
  // knows about AI decides how she explains everything afterwards — what she
  // can assume, how much she spells out, whether "I'll text them back" needs
  // unpacking. Same rule as asking a property investor how many deals he has
  // done before teaching him anything (8/12).
  // ai_familiar / invite / after_demo / offer WERE HERE and are deleted, not
  // commented out (8/15). They were the quiz, the party trick and the pitch:
  //   "its kinda wired how she talks, just want her to be an assitant"
  // A dead node left in a graph is a node something eventually routes back
  // into - the `who` step proved that, still answering a question she had
  // stopped asking a day after it was cut. If a turn is wrong, remove it.

  trade: {
    say: 'What kind of work do you do?',
    next(text) {
      if (isTooRichForACannedLine(text)) return null;
      const trade = detectTrade(text);
      if (trade) return { node: 'confirm_build', data: { trade } };
      if (String(text || '').trim().length > 2) {
        return { node: 'confirm_build', data: { trade: String(text).trim().slice(0, 40) } };
      }
      return null;
    },
  },

  confirm_build: {
    say: (d) => `${d.trade ? d.trade[0].toUpperCase() + d.trade.slice(1) : 'Got it'} — I can look you up and build your setup right now. Want me to?`,
    next(text) {
      const yn = readYesNo(text);
      if (yn === 'yes') return { action: 'openBusinessSetup' };
      if (yn === 'no') return { node: 'pain' };
      return null;
    },
  },

  // ── HIS ANSWER GOES TO THE SERVER, WHICH STARTS THE JOB (8/16) ──────────
  //
  //   "she needs to get an idea of this users situation before she talks about
  //    problems"
  //
  // This used to read four keyword patterns and NAVIGATE — "phone" opened the
  // leads page. Opening a page is not doing the job, and the client's four
  // patterns are a much blunter instrument than what now exists server-side:
  // ariaTaskIntent covers eight real problems and answers each with the
  // automation that fixes it plus the one fact needed to switch it on.
  //
  // So she returns null and lets the turn go up. "the phone rings while I'm
  // under a sink" comes back as "That's the first thing I'd switch on — I text
  // back everyone you miss. What number do they call?" rather than a page.
  // ── PLAIN QUESTIONS, IN ORDER (Braeden 8/16) ────────────────────────────
  //
  //   "can she stop saying whats eating most of your time, just ask simple
  //    questions, and go through a get to know you list, simple standard
  //    questions"
  //
  // "What's eating most of your time?" is a consultant's question. It asks
  // someone to diagnose their own business before they have any reason to
  // trust her, and it is open-ended enough that half of them stall on it.
  //
  // These are the plain ones anybody can answer in three words, asked one at a
  // time in the order that unlocks the most. Each answer switches something on.
  pain: {
    say: 'What do customers call you on?',
    next() { return null; },
    fallbackTo: 'open',
  },

  done: {
    say: "All good. I'm here.",
    next() { return { action: 'end' }; },
  },
};

// ── THE ASIDES (8/14) ──────────────────────────────────────────────────────
//
//   "we need to make responses for her so she has them ready to go depending on
//    what the user says so she can have an almost instant reply"
//
// A new person does not answer the question you asked. They ask their own —
// what is this, what does it cost, are you a robot, does this actually work.
// Before this, every one of those fell through to the brain and stalled for
// seconds on the very first impression.
//
// Each aside answers in ONE short statement and no question, because the
// caller re-asks whatever she was in the middle of. One question per turn,
// and the thread never drops.
//
// Order matters — first match wins, so put the specific ones above the general.
// Exported so the audio renderer can pre-voice every one of them. These are the
// lines she says INSTANTLY, which is precisely why they cannot be waiting on a
// live synth (8/15).
export const ASIDES = [
  // --- confusion: she repeats herself instead of answering something new ---
  { id: 'repeat', test: /^(what|huh|sorry|pardon|come again|say (that )?again|repeat( that)?|didn'?t (catch|hear) (that|you)|one more time)[\s?!.]*$/i,
    repeat: true },

  // --- money. The one thing every owner asks first. ---
  // ABOUT HER PRICE, NOT ABOUT PRICES. A bare \bprice\b caught "what is the
  // current price of copper pipe" and answered "Nothing today — you're not
  // signing anything to look" (8/16). Harmless inside the first-run flow, where
  // the only subject is her; actively wrong now these answer every turn, where
  // an owner asking about materials is a completely normal thing to say.
  // Every alternative below has to be about YOU or THIS.
  { id: 'price', test: /\b(how much (is |does )?(this|it|you|aria)\b|how much do(es)? (this|it|you)|what.s? (it|this) cost|your (price|pricing|rate|fee)|(price|pricing|cost) (of )?(this|it|the app|aria)|do you charge|what do you charge|is (this|it) expensive|can i afford (this|it)|per month|monthly (fee|cost)|subscription (cost|price))\b/i,
    say: "Nothing today. Later it's pay-as-you-go, and you see the number first." },
  { id: 'free', test: /\b(is (it|this) free|free\?|no charge|trial)\b/i,
    say: "Free to look. You pay once I'm working jobs for you." },
  // "i dont want to sign anything" is the most common way this worry is
  // spoken, and it matched none of the original words (8/16).
  { id: 'contract', test: /\b(contract|lock(ed)? in|commit|cancel any ?time|sign (up for|up|anything)|don'?t want to sign|dont want to sign)\b/i,
    say: "No contract. Leave whenever." },

  // --- what she is ---
  // "what CAN you do" is not "what DO you do" — one word apart and it was
  // falling through to the brain, which answered with a generic assistant list
  // (reminders, chatbots, form-filling) that has nothing to do with her (8/16).
  { id: 'whatisthis', test: /\b(what.?s? this|what is this|what do you do|what can you do|what can you help( me)? with|what do you offer|what are you (for|good for)|what are you|explain this|what.?s? the app)\b/i,
    // "I answer your phone" was false — she cannot talk on a call. Texting back
    // the ones you miss is the real thing, and it is still the first thing
    // worth naming (8/16).
    // 8/23: "I run the office side" is the retired identity - ARIA_IDENTITY
    // v1.1 rule 0: she is HIS AI, and the businesses are things she helps
    // with, not what she is.
    say: "Whatever you're working on. Your PC, your businesses, research, writing - I dig in and I learn as we go. What do you need?" },
  { id: 'bot', test: /\b(are you (a )?(real|human|person|bot|robot|ai)|is this (a )?(bot|robot|ai|real)|you.re (a )?(bot|robot|ai))\b/i,
    say: "I'm an AI. Yours. I help with whatever you're working on." },
  { id: 'name', test: /\b(who are you|your name|what.s your name)\b/i,
    say: "Aria. Your AI - I help with whatever you're working on." },
  { id: 'howwork', test: /\b(how does (it|this) work|how do you work|how would (it|that) work)\b/i,
    say: "You tell me what you need, I go do it - and I keep what I learn." },
  { id: 'skeptic', test: /\b(does (it|this) (actually|really) work|too good|scam|legit|prove it|bs|bullshit|sounds fake)\b/i,
    say: "Fair question. I'll build it on your real business — costs you nothing to look." },

  // --- capability questions, answered honestly ---
  { id: 'phone', test: /\b(answer\w* (my|the) phone|take calls?|missed calls?|voicemail|phone ring\w*|pick up the phone)\b/i,
    // ── SHE DOES NOT TALK ON THE PHONE (Braeden 8/16) ────────────────────
    //   "One thing she cant do yet is talk on the phone for people, she has to
    //    be realistic on her featrues"
    //
    // "Yes. I pick up" was a straight lie, and the worst kind: it is the FIRST
    // capability she names, so an owner signs up believing his phone is
    // covered, misses a call, and finds out she never answered it. Texting back
    // a missed call is genuinely valuable and it is what she actually does —
    // it just has to be said instead of dressed up as answering.
    say: "Can't talk on calls yet. But I text back everyone you miss." },
  // A bare `quotes?` stole "can you give me quotes on lumber" — a supplier
  // question, answered with her invoicing pitch (8/16). Quoting has to be
  // something SHE does or something HE sends, never any mention of the word.
  { id: 'invoice', test: /\b(invoic\w*|bill(ing|s)?|get(ting)? paid|collect\w*|receipts?|(my|your|the|send|write|do|doing) (estimates?|quotes?)|quoting|chase (the )?money|money (i'?m|im) owed)\b/i,
    say: "I quote it, invoice it when it's done, and chase it until it's paid." },
  { id: 'website', test: /\b(website|web site|my site|landing page)\b/i,
    say: "I'll build you one, or take over the one you've got." },
  // "can you google that for me" is a REQUEST TO USE THE SEARCH ENGINE, not a
  // question about her Google Business listing — and a bare \bgoogle\b answered
  // it with a canned line about reviews (8/16). Inside the first-run flow that
  // could not happen; now these answer every turn it happens constantly. It has
  // to be about HIS LISTING, never about the act of searching.
  { id: 'google', test: /\b(my (google|listing|reviews?|maps)|google (business|listing|profile|my business)|google maps|(the )?reviews?( on google)?\b|business listing|my rank(ing)?)\b/i,
    say: "Reviews and the listing are mine. The Google connection isn't wired yet though." },
  // `slow` on its own caught "my computer is slow" and answered with a
  // pitch about winning more work. Slow WHAT matters (8/16).
  { id: 'leads', test: /\b(more (work|jobs|customers|leads)|find (me )?(work|jobs|leads)|advertis|marketing|(work|jobs|business|phone|summer|winter) (is |has been )?slow|slow (season|month|year|lately|right now))\b/i,
    say: "Answer fast, follow up, stop losing the ones who already called." },
  { id: 'schedule', test: /\b(schedul\w*|calendar|book(ing)? jobs?|dispatch\w*|routes?)\b/i,
    say: "Jobs go on your calendar with reminders, ordered by drive time." },
  { id: 'taxes', test: /\b(tax\w*|1099|w-?2|cpa|accountant|bookkeep\w*|quickbooks)\b/i,
    say: "I categorise every dollar as it happens. I don't file for you." },
  // "guys" was bare here until 9/6, and "you guys" is how a customer opens a
  // sentence to a business. Measured live on the real route:
  //   "do you guys do piano moving"
  //     -> "I track their hours per job. That's what payroll comes off."
  // The most ordinary question a customer asks, answered with a payroll line.
  // This aside is about HIS crew, so the possessive has to be there - the same
  // shape of bug as `sue` matching inside "issue a refund" (9/6, features.js).
  { id: 'crew', test: /\b(crew|employees?|(?:my|our|the) guys|workers?|payroll|hir(e|ing))\b/i,
    say: "I track their hours per job. That's what payroll comes off." },

  // --- practical ---
  { id: 'account', test: /\b(sign ?up|create an account|make an account|need an account|log ?in|password|register)\b|\b(?:my |your |an? )?(?:email|e-mail)\b[^.?!]{0,20}\b(?:required|need(?:ed)?|to sign|signup|sign up|account|register)\b/i,
    say: "No signup to look around." },
  { id: 'howlong', test: /\b(how long|take long|quick|time does (it|this) take|minutes?)\b/i,
    say: "About a minute. You can stop any time." },
  { id: 'privacy', test: /\b(privacy|my data|record(ing)?|listening|spy|safe|secure|share my)\b/i,
    say: "Your data stays yours. I don't sell it." },
  // BARE CITY NAMES REMOVED (8/26). This matched the single word "wichita", so
  // "how much do movers charge in wichita" — a straight market-rate question —
  // came back as "Kansas. TTA, out of Wichita." The aside is meant for someone
  // asking where SHE is; it has to be asked that way to fire.
  { id: 'where', test: /\b(where are you|where(?:'s| is) (?:aria|this|the company)|are you (?:located|based)|what city are you|based (?:in|out of))\b/i,
    say: "Kansas. TTA, out of Wichita." },

  // --- social ---
  { id: 'greet', test: /^(hey|hi|hello|yo|sup|howdy|good (morning|afternoon|evening))\b/i,
    say: "Hey." },
  { id: 'thanks', test: /\b(thanks|thank you|appreciate)\b/i,
    say: "Anytime." },
  // must be the WHOLE utterance — a bare "stop" inside a sentence about
  // stopping residential work is not someone saying goodbye (8/14)
  { id: 'bye', test: /^(not interested|no thanks,? bye|leave me alone|stop|go away|bye|goodbye|later|nevermind|never mind)[\s.!]*$/i,
    say: "All good. I'm here.", end: true },
];

/**
 * Did they ask something instead of answering? Returns the instant reply, or
 * null to let the flow (then the brain) handle it.
 *   { say }        → say this, then re-ask what she was asking
 *   { repeat }     → say her own last line again, slower
 *   { end }        → they're done; stop the flow
 */
// A canned line is only ever right for a SHORT, SINGLE question. The moment
// someone says something with a goal or a contrast in it — "i have a website
// and google but i want to rebrand" — a keyword match is worse than no answer,
// because she confidently replies to a question he never asked and drops the
// thing he actually said. Caught 8/14, and it was mangling real sentences:
//   "...but i want to rebrand"            → "I'll build you a website!"
//   "...thinking of splitting them"       → "Junk removal — want me to build it?"
//   "i want to stop doing residential"    → matched goodbye and ENDED the chat
// Anything below goes to the brain, which can actually reason about it.
const COMPOUND = /\b(but|however|though|although|instead|rather than|as well as|on top of)\b/i;
const INTENT = /\b(i want|i'?d like|i need|im thinking|i'?m thinking|thinking (of|about)|planning|trying to|going to|gonna|hoping to|considering|looking to|about to|used to|switch|rebrand|rename|split|merge|expand|move away)\b/i;

export function isTooRichForACannedLine(text) {
  const t = String(text || '').trim();
  const words = t.split(/\s+/).filter(Boolean).length;
  if (COMPOUND.test(t) || INTENT.test(t)) return true;
  // two or more "and"-joined clauses is a compound thought, not a question
  if ((t.match(/\band\b/gi) || []).length >= 2) return true;
  return words > 9;
}

// WHERE A CANNED LINE IS NEVER RIGHT, WHATEVER THEY SAY. At `invite` she has
// just asked them to test her on computers. Answering that with a stock line is
// the worst possible first impression — "i forgot my password what do i do"
// matched the signup aside and she talked about creating an account instead of
// helping (8/14). The demo turn belongs to the brain, always.
// EMPTY ON PURPOSE (8/15). This existed to protect the demo turn at `invite`,
// and `invite` is gone. Now that she opens with "what can I help you with", an
// aside is exactly right on turn one: someone whose first words are "how much
// does this cost" should get that answered instantly, not sent to the brain.
// Left in place because the NEXT scripted turn will need it again.
const NO_ASIDES_AT = new Set([]);

export function matchAside(text, nodeKey) {
  const t = String(text || '').trim();
  if (!t) return null;
  if (nodeKey && NO_ASIDES_AT.has(nodeKey)) return null;
  if (isTooRichForACannedLine(t)) return null;   // let her actually think
  for (const a of ASIDES) {
    if (a.test.test(t)) return { id: a.id, say: a.say || null, repeat: !!a.repeat, end: !!a.end };
  }
  return null;
}

/** How she picks the question back up — varied, so it isn't a stuck record. */
export function reAsk(nodeKey, data, timesAlready) {
  const q = lineFor(nodeKey, data);
  if (!q) return null;
  if (!timesAlready) return q;
  // she already repeated this once — shorten rather than say it verbatim again
  // Only the nodes that ACTUALLY ask something. `who` and `looking` were still
  // listed here long after those steps were cut - a shortened re-ask for a
  // question she no longer asks is how "Do you run the business?" kept coming
  // back from the dead (8/15).
  const short = {
    trade: 'What kind of work do you do?',
    confirm_build: 'Want me to build it?',
    pain: "What's eating the most time right now?",
  };
  return short[nodeKey] || q;
}

/** What she says at a node, with any captured data folded in. */
export function lineFor(nodeKey, data) {
  const n = NODES[nodeKey];
  if (!n) return null;
  return typeof n.say === 'function' ? n.say(data || {}) : n.say;
}

/**
 * Advance the flow. Returns:
 *   { say, node }             → speak this next, stay in the flow
 *   { action, data }          → do this, flow ends
 *   { toBrain: true, node }   → off-script; let the brain answer this turn,
 *                               then re-ask `node` so the thread is not lost
 */
export function advance(nodeKey, text, data) {
  const n = NODES[nodeKey];
  if (!n) return { action: 'end' };
  const r = n.next(text, data || {});
  if (!r) return { toBrain: true, node: n.fallbackTo || nodeKey };
  if (r.action) return { action: r.action, data: { ...(data || {}), ...(r.data || {}) } };
  const merged = { ...(data || {}), ...(r.data || {}) };
  // THE DEMONSTRATION TURN. She just said "ask me anything about computers" —
  // so the BRAIN has to answer what they asked. Without this she moved straight
  // to the next node and said her next line INSTEAD of answering, which means
  // she invited a question and then ignored it. Every demo conversation in the
  // battery failed on exactly that (8/14).
  if (r.toBrainFirst) return { toBrain: true, node: r.node, data: merged };
  return { node: r.node, say: lineFor(r.node, merged), data: merged };
}
